import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Treatment } from '@enums/treatment';
import { environment } from '@environments/environment';

import { ConsultationRequestService } from './consultation-request.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Define all the forms and allow their data to be shared between components.
 */
export class FormService {
  /**
   * The checkout form definition.
   */
  public checkout: FormGroup;

  /**
   * The consultation request form definition.
   */
  public consultationRequest: FormGroup;

  /**
   * The consultation request form definition.
   */
  public consultationRequestForm: FormGroup;

  /**
   * The contact information form definition.
   */
  public addressContactInformation: FormGroup;

  /**
   * The treatment preferences form definition.
   */
  public treatmentPreferencesForm: FormGroup;

  private fb = inject(FormBuilder);

  /**
   * The form group definition for the guardian form.
   */
  private patientGuardianParentForm: FormGroup;

  /**
   * The patient information form definition.
   */
  private patientInfo: FormGroup;

  /**
   * The order medical history form definition.
   */
  private orderMedicalHistory: FormGroup = this.fb.group({
    onMedication: new FormControl(null),
    medicationDetails: new FormControl(null),
    hasAllergies: new FormControl(null),
    allergiesDetails: new FormControl(null),
    hasOtherConditions: new FormControl(null),
    otherConditionsDetails: new FormControl(null),
  });

  /**
   * The order page service agreement form definition.
   */
  private serviceAgreement: FormGroup = this.fb.group({
    agreed: new FormControl(null),
  });

  /**
   * The consultation request personal information form definition.
   */
  private consultationRequestPersonalInfo: FormGroup = this.fb.group({
    firstName: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
    lastName: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
    birthday: this.getDateForm(),
    height_feet: new FormControl(null),
    height_inches: new FormControl(null),
    weight: new FormControl(null),
  });

  /**
   * The consultation request contact information form definition.
   */
  private consultationRequestContactInfo: FormGroup = this.fb.group({
    email: new FormControl(null, [Validators.required, Validators.email]),
    phone: new FormControl(null, [Validators.required, Validators.pattern('[0-9]{10}')]),
  });

  /**
   * The consultation request payment information form definition.
   */
  private consultationRequestPaymentInfo: FormGroup = this.fb.group({
    discount: new FormControl(0),
  });

  /**
   * The consultation request medical history form definition.
   */
  private consultationRequestMedicalHistory: FormGroup = this.fb.group({
    allergies: new FormControl(null, [Validators.required, Validators.maxLength(1000)]),
    prescriptions: new FormControl(null, [Validators.required, Validators.maxLength(1000)]),
    race: new FormControl(null),
    ethnicity: new FormControl(null),
    sex_with: new FormControl(null, [Validators.required]),
    pregnant: new FormControl(null),
    menstrual_d: this.getDateForm(false),
    due_d: this.getDateForm(false),
    symptoms: this.getFormGroupFromSymptoms(),
    health_info: new FormControl(null),
  });

  /**
   * The consultation request prescription partner form definition.
   */
  private partnerPrescriptionForm: FormGroup = this.fb.group({
    firstName: new FormControl(null),
    lastName: new FormControl(null),
    gender: new FormControl(null),
    pregnant: new FormControl(null),
    birthday: this.getDateForm(false),
    allergies: new FormControl(null),
    prescriptions: new FormControl(null),
    symptoms: this.getFormGroupFromSymptoms(),
    height_feet: new FormControl(null),
    height_inches: new FormControl(null),
    weight: new FormControl(null),
  });

  /**
   * The consultation request prescription information form definition.
   */
  private consultationRequestPrescriptionInfo: FormGroup = this.fb.group({
    treatment: new FormControl(Treatment.Self, Validators.required),
    partner: this.partnerPrescriptionForm,
  });

  /**
   * The consultation request patient information form definition.
   */
  private consultationRequestPatientInfo: FormGroup = this.fb.group({
    first_name: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
    last_name: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
    email: new FormControl(null, [Validators.email]),
    birthday: this.getDateForm(),
    gender: new FormControl(null),
    height_feet: new FormControl(null),
    height_inches: new FormControl(null),
    weight: new FormControl(null),
  });

  /**
   * Initialize the service.
   */
  public constructor() {
    this.addressContactInformation = this.fb.group({
      phone: this.fb.group({
        number: new FormControl(null, [Validators.required, Validators.pattern('[0-9]{10}')]),
        voicemail_allowed: new FormControl(false, [Validators.required]),
        sms_allowed: new FormControl(false, [Validators.required]),
      }),
      address: this.address,
      tos: new FormControl(false, [Validators.requiredTrue]),
    });

    this.treatmentPreferencesForm = this.fb.group({
      treatmentPreferences: new FormControl(null),
    });

    this.patientGuardianParentForm = this.fb.group({
      firstName: new FormControl(),
      lastName: new FormControl(),
      relationToChild: new FormControl(),
      attestation: new FormControl(),
    });

    this.patientInfo = this.fb.group({
      firstName: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
      lastName: new FormControl(null, [Validators.required, Validators.minLength(environment.minimumNameLength)]),
      sex: new FormControl(null, [Validators.required]),
      birthday: this.getDateForm(),
      contactVia: new FormControl('', [Validators.required]),
      phone: new FormControl(null),
      voicemail_allowed: new FormControl(false),
      email: new FormControl(null, [Validators.email]),
      partnerEmail: new FormControl(null, [Validators.email]),
      hasPartner: new FormControl(false),
      parentGuardian: this.patientGuardianParentForm,
      insuranceType: new FormControl(null),
    });

    this.checkout = this.fb.group({
      lab_id: ['', Validators.required],
      booking_id: [''],
      patient: this.patientInfo,
      medicalHistory: this.orderMedicalHistory,
      serviceAgreement: this.serviceAgreement,
    });

    this.consultationRequest = this.fb.group({
      personal: this.consultationRequestPersonalInfo,
      contact: this.consultationRequestContactInfo,
      pharmacy: this.getPharmacyFormGroup(),
      prescription: this.consultationRequestPrescriptionInfo,
      payment: this.consultationRequestPaymentInfo,
      medicalHistory: this.consultationRequestMedicalHistory,
      terms: new FormControl(false, Validators.requiredTrue),
      additional_info: new FormGroup({}),
    });

    this.consultationRequestForm = this.fb.group({
      patient: this.consultationRequestPatientInfo,
      pharmacy: this.getPharmacyFormGroup(),
      address: new FormGroup({
        ...this.address.controls,
        streetAddress2: new FormControl(''),
      }),
    });
  }

  /**
   * The default address form definition.
   */
  public get address(): FormGroup {
    return new FormGroup({
      streetAddress: new FormControl(null, [Validators.required]),
      city: new FormControl(null, [Validators.required]),
      state: new FormControl(null, [Validators.required]),
      zipcode: new FormControl(null, [
        Validators.required,
        Validators.pattern('^[0-9]{5}$|^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$'),
      ]),
    });
  }

  /**
   * The default ED treatment form definition.
   */
  public get edTreatment(): FormGroup {
    return new FormGroup({
      treatment_type: new FormControl(null, [Validators.required]),
    });
  }

  /**
   * The upsell card form definition.
   */
  public getUpsellCardForm(): FormGroup {
    return this.fb.group({
      upsell: new FormControl(null, [Validators.required]),
    });
  }

  /**
   * Clears control errors and validators.
   *
   * @param {AbstractControl} control Form control
   */
  public resetControl(control: AbstractControl): void {
    control.clearValidators();
    control.setErrors(null);
    control.updateValueAndValidity();
  }

  /**
   * Sets the validator and run the validation.
   *
   * @param {AbstractControl} control  Form control
   * @param {ValidatorFn[]} validators Form validators array
   */
  public setValidator(control: AbstractControl, validators: ValidatorFn[]) {
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  /**
   * Dynamically returns the phone form group.
   */
  public orderPhoneForm(): FormGroup {
    return this.fb.group({
      number: new FormControl(''),
      voicemail_allowed: new FormControl(false),
      sms_allowed: new FormControl(false),
    });
  }

  /**
   * Validates that at least one control in the group has a true value.
   *
   * @returns a ValidatorFn that returns the error symptomsRequired if
   * the condition is not met, null otherwise
   */
  public symptomSelectedValidator(): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const invalid = !Object.values(group.value).some((value) => value);
      return invalid ? { symptomsRequired: true } : null;
    };
  }

  /**
   * Recursively disables all the controls within an AbstractControl (FormGroup, FormControl, or FormArray).
   *
   * @param control the control to disable
   */
  public disableAllControls(control: AbstractControl): void {
    if (control instanceof FormControl) {
      control.disable();
    } else if (control instanceof FormGroup) {
      Object.values(control.controls).forEach((childControl) => this.disableAllControls(childControl));
    } else if (control instanceof FormArray) {
      control.controls.forEach((arrayControl) => this.disableAllControls(arrayControl));
    }
  }

  /**
   * Creates a form group from the possible symptoms.
   *
   * @returns a FormGroup with one control per symptom and all values set to false
   */
  private getFormGroupFromSymptoms(): FormGroup {
    const symptomsFormGroup = new FormGroup({});

    ConsultationRequestService.symptoms.forEach((symptom) => {
      symptomsFormGroup.addControl(symptom.value, new FormControl(false));
    });

    return symptomsFormGroup;
  }

  /**
   * Creates a pharmacy information form.
   *
   * @returns a FormGroup to enter the pharmacy information
   */
  private getPharmacyFormGroup(): FormGroup {
    const pharmacyFormGroup = new FormGroup({});

    pharmacyFormGroup.addControl('id', new FormControl(null, Validators.required));
    pharmacyFormGroup.addControl('name', new FormControl(null));
    pharmacyFormGroup.addControl('phone', new FormControl(null));
    pharmacyFormGroup.addControl('state', new FormControl(null));
    pharmacyFormGroup.addControl('address', new FormControl(null));
    pharmacyFormGroup.addControl('address2', new FormControl(null));
    pharmacyFormGroup.addControl('city', new FormControl(null));
    pharmacyFormGroup.addControl('title', new FormControl(null));
    pharmacyFormGroup.addControl('zip_code', new FormControl(null));

    return pharmacyFormGroup;
  }

  /**
   * Creates a date form group with its month, day and year controls.
   *
   * @param addRequiredValidators whether or not to add the required validator to each control
   * @returns a FormGroup to enter the date information
   */
  private getDateForm(addRequiredValidators: boolean = true): FormGroup {
    const validator = addRequiredValidators ? Validators.required : null;

    return new FormGroup({
      month: new FormControl(null, validator),
      day: new FormControl(null, validator),
      year: new FormControl(null, validator),
    });
  }
}
